import { useCallback, useEffect, useRef } from 'react';
import type { UseClickOutsideHook } from '../types/useClickOutsideTypes';

/**
 * This hook simplifies tracking clicks outside of a specific DOM ref. When clicked outside, callback is invoked with the mouse event arguments.
 * @param contentRef Ref to the DOM content being checked
 * @param clickOutsideCallback Callback function for when clicked outside ref DOM with optional MouseEvent args
 * @param options Optional behaviors for this hook. ignoreClick defaults to false.
 */
export const useClickOutside: UseClickOutsideHook = (contentRef, clickOutsideCallback, options) => {
    const opts = options || {};

    const ignoreClick = opts.ignoreClick || (() => false);
    const ignoreInitialClick = opts.ignoreInitialClick ?? true;
    const isInitialClickRef = useRef(ignoreInitialClick);

    const handleBodyClick = useCallback(
        (e: MouseEvent) => {
            if (isInitialClickRef.current) {
                isInitialClickRef.current = false;
                return;
            }

            // istanbul ignore next
            if (ignoreClick()) {
                return;
            }

            // Note: this is verbose by design.
            // istanbul ignore next
            const contentRect = contentRef.current?.getClientRects()?.[0];
            // istanbul ignore next
            if (!contentRect) {
                return;
            }

            // if inside this content, then normal toggle
            // if outside this content, toggle off
            const x = e.clientX;
            const y = e.clientY;
            // istanbul ignore next
            const clickedInsideContent =
                x >= contentRect.left && x <= contentRect.right && y >= contentRect.top && y <= contentRect.bottom;
            // istanbul ignore next
            if (clickedInsideContent) {
                return;
            }

            clickOutsideCallback(e);
        },
        [contentRef, clickOutsideCallback, ignoreClick]
    );

    useEffect(() => {
        isInitialClickRef.current = ignoreInitialClick;

        document.body.addEventListener('click', handleBodyClick);
        return () => {
            document.body.removeEventListener('click', handleBodyClick);
        };
    }, [handleBodyClick, ignoreInitialClick]); // basically on init
};
